import { useEffect, useState } from 'react';

import { Divider, type TypeSelectOption, dialogService, toastService } from '@thefarmersfront/kitchen';

import type { IRowNode } from 'ag-grid-community';
import dayjs from 'dayjs';

import { DialogMessage, PageTitle } from '~/components/common';
import {
  DeliveryAgencyExcelDownloadModal,
  DeliveryAgencyOrderDetailModal,
  DeliveryAgencyOrderExcelUploadFailModal,
  DeliveryAgencyOrderExcelUploadModal,
  DeliveryAgencyOrderFilter,
  DeliveryAgencyOrderTable,
} from '~/components/delivery-agency';
import { PageWrapper } from '~/components/layout';

import { useDownloadReady, useModal, useToggle } from '~/hooks/common';
import {
  useGetOrders,
  useGetOrdersOrderId,
  useGetOrdersSearchConditions,
  useGetTemplatesDownloadUrl,
  usePostOrdersCancel,
  usePostOrdersDownload,
  usePostOutboundsUnits,
} from '~/hooks/delivery-agency/api';
import { addIf, renderFilterErrorMessage, transformToSelectOptions } from '~/utils/common';
import { formatDate, getDateByOffset } from '~/utils/date';

import {
  BREAD_CRUMB,
  DELIVERY_AGENCY,
  DELIVERY_AGENCY_ORDER_STATUS,
  INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
  INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
  MAX_DELIVERY_AGENCY_DATE_RANGE,
  OPTION_ALL,
  PATH,
  TOAST_MESSAGE,
  TypeDeliveryAgencyOrderStatus,
} from '~/constants';

import type {
  TypeDeliveryAgencyOrder,
  TypeDeliveryAgencyOrderDetail,
  TypeDeliveryAgencyOrderFilterOptions,
  TypeDeliveryAgencyOrderFilterValues,
  TypeDeliveryAgencyOrderFilterValuesStatus,
  TypeDeliveryAgencyOrdersUploadResult,
  TypeGetOrdersParams,
  TypePostOrdersDownloadParams,
} from '~/types/delivery-agency.types';

const INITIAL_SEARCH_FILTER_VALUES: TypeDeliveryAgencyOrderFilterValues = {
  keywordType: null,
  keyword: '',
  deliveryDate: { start: getDateByOffset(1, 'day'), end: getDateByOffset(1, 'day') },
  orderStatus: OPTION_ALL,
  deliveryType: OPTION_ALL,
  courier: OPTION_ALL,
  sortBy: null,
  pagination: {
    currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
    totalCount: 0,
    pageSize: INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
  },
};

const DeliveryAgencyOrderPage = () => {
  const { requestCodes } = useGetOrdersSearchConditions();
  const { requestOrders } = useGetOrders();
  const { requestOrderDetail } = useGetOrdersOrderId();
  const { requestTemplatesDownloadUrl } = useGetTemplatesDownloadUrl();
  const { requestOutboundUnitsConfirm } = usePostOutboundsUnits();
  const { requestOrdersCancel } = usePostOrdersCancel();
  const { requestOrdersDownload } = usePostOrdersDownload();

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const { isDownloadReady, searchedFilter, markDownloadReady, checkFilterChange } = useDownloadReady({
    initialFilter: INITIAL_SEARCH_FILTER_VALUES,
  });

  const [searchFilterOptions, setSearchFilterOptions] = useState<TypeDeliveryAgencyOrderFilterOptions>({
    searchKeywordTypes: [],
    orderStatuses: [],
    deliveryTypes: [],
    courierCodes: [],
    sortOptions: [],
  });

  const [searchFilterValues, setSearchFilterValues] = useState<TypeDeliveryAgencyOrderFilterValues>({
    ...INITIAL_SEARCH_FILTER_VALUES,
  });
  const [searchFilterValuesStatus, setSearchFilterValuesStatus] = useState<TypeDeliveryAgencyOrderFilterValuesStatus>({
    keyword: 'default',
    deliveryDate: 'default',
  });

  const [ordersData, setOrdersData] = useState<TypeDeliveryAgencyOrder[] | null>(null);
  const [selectedItems, setSelectedItems] = useState<TypeDeliveryAgencyOrder[]>([]);

  const [orderDetailData, setOrderDetailData] = useState<TypeDeliveryAgencyOrderDetail | null>(null);

  const [orderExcelUploadFailData, setOrderExcelUploadFailData] = useState<TypeDeliveryAgencyOrdersUploadResult | null>(
    null
  );

  useEffect(() => {
    requestCodes({
      onSuccess: (data) => {
        setSearchFilterOptions({
          searchKeywordTypes: transformToSelectOptions(data.searchKeywordTypes, 'code', 'description'),
          orderStatuses: [OPTION_ALL, ...transformToSelectOptions(data.orderStatuses, 'code', 'description')],
          deliveryTypes: [OPTION_ALL, ...transformToSelectOptions(data.deliveryTypes, 'code', 'description')],
          courierCodes: [OPTION_ALL, ...transformToSelectOptions(data.courierCodes, 'code', 'description')],
          sortOptions: transformToSelectOptions(data.sortOptions, 'code', 'description'),
        });

        setSearchFilterValues({
          ...INITIAL_SEARCH_FILTER_VALUES,
          keywordType: transformToSelectOptions(data.searchKeywordTypes, 'code', 'description')[0] ?? null,
          sortBy: transformToSelectOptions(data.sortOptions, 'code', 'description')[0] ?? null,
        });
      },
      onError: (error) => {
        toastService.addToast({
          type: 'error',
          persist: true,
          message: error?.message || renderFilterErrorMessage(),
        });
      },
    });
  }, []);

  useEffect(() => {
    checkFilterChange(searchFilterValues);
  }, [searchFilterValues]);

  const deliveryOrderDetailModal = useModal();

  const orderExcelDownloadModal = useModal();

  const orderExcelUploadModal = useModal({
    onConfirm: (data?: TypeDeliveryAgencyOrdersUploadResult) => {
      if (!data) return;

      if (data.failCount > 0) {
        setOrderExcelUploadFailData(data);
        orderExcelUploadFailModal.handleOpenModal();
      } else {
        toastService.addToast({ message: '등록되었습니다.\n목록에 표시되지 않으면 새로고침 후 확인해 주세요.' });
        handleSearch();
      }
    },
  });

  const orderExcelUploadFailModal = useModal();

  const handleResetFilterValues = () => {
    setSearchFilterValues({
      ...INITIAL_SEARCH_FILTER_VALUES,
      keywordType: searchFilterOptions.searchKeywordTypes[0] ?? null,
      pagination: searchFilterValues.pagination,
      sortBy: searchFilterValues.sortBy,
    });
    setSearchFilterValuesStatus({
      keyword: 'default',
      deliveryDate: 'default',
    });
  };

  const handleChangeSort = (value: TypeSelectOption<string> | null) => {
    handleSearch({
      ...searchFilterValues,
      sortBy: value,
      pagination: {
        ...searchFilterValues.pagination,
        currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
      },
    });
  };

  const handleChangePageLimit = (value: TypeSelectOption<number>) => {
    handleSearch({
      ...searchFilterValues,
      pagination: {
        ...searchFilterValues.pagination,
        currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
        pageSize: value.value,
      },
    });
  };

  const handleChangePage = (value: number) => {
    handleSearch({
      ...searchFilterValues,
      pagination: { ...searchFilterValues.pagination, currentPage: value },
    });
  };

  const handleChangeRowSelection = (selectedRows: IRowNode<TypeDeliveryAgencyOrder>[]) => {
    const selectedItems = selectedRows.map((row) => row.data).filter((item) => !!item);
    setSelectedItems(selectedItems);
  };

  const handleSearch = (filterValues: TypeDeliveryAgencyOrderFilterValues = searchFilterValues) => {
    const isDateRangeEmpty = !filterValues.deliveryDate?.start && !filterValues.deliveryDate?.end;

    if (filterValues.keyword.trim() === '' && isDateRangeEmpty) {
      dialogService.addDialog({
        children: '[출고번호, 판매처 주문번호, 수령자명, 주문자명, 배송예정일] 중 1개 이상 입력 후 검색해주세요.',
      });
      return;
    }

    const dateDiff = dayjs(filterValues.deliveryDate?.end).diff(filterValues.deliveryDate?.start, 'day');
    const isDateLimitExceeded = dateDiff >= MAX_DELIVERY_AGENCY_DATE_RANGE;

    if (isDateRangeEmpty || isDateLimitExceeded) {
      dialogService.addDialog({
        children: `최대 ${MAX_DELIVERY_AGENCY_DATE_RANGE}일로 입력 후 검색해주세요.`,
      });
      setSearchFilterValuesStatus((prev) => ({ ...prev, deliveryDate: 'error' }));
      return;
    }

    const params: TypeGetOrdersParams = {
      searchKeywordType: filterValues.keywordType?.value ?? '',
      searchKeyword: filterValues.keyword,
      ...addIf(formatDate(filterValues.deliveryDate?.start, 'YYYY-MM-DD'), 'startDate'),
      ...addIf(formatDate(filterValues.deliveryDate?.end, 'YYYY-MM-DD'), 'endDate'),
      ...addIf(filterValues.orderStatus?.value, 'orderStatus'),
      ...addIf(filterValues.deliveryType?.value, 'deliveryType'),
      ...addIf(filterValues.courier?.value, 'courierCode'),
      sortBy: filterValues.sortBy?.value ?? '',
      cursor: filterValues.pagination.currentPage,
      limit: filterValues.pagination.pageSize,
    };

    requestOrders(params, {
      onSuccess: ({ data, pagination }) => {
        setOrdersData(data);

        const updatedFilterValues = {
          ...filterValues,
          pagination: {
            ...filterValues.pagination,
            totalCount: pagination.listTotal,
            pageSize: pagination.listView,
            currentPage: pagination.cursor,
          },
        };
        setSearchFilterValues(updatedFilterValues);

        markDownloadReady({ filterValue: updatedFilterValues, hasData: data.length > 0 });
      },
      onError: (error) => {
        toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
      },
    });
  };

  const handleOpenDetailModal = (order: TypeDeliveryAgencyOrder) => {
    requestOrderDetail(
      { orderId: order.orderId },
      {
        onSuccess: (data) => {
          setOrderDetailData(data);
          deliveryOrderDetailModal.handleOpenModal();
        },
        onError: (error) => {
          toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  const handleOrderRequestCancel = () => {
    if (selectedItems.length === 0) {
      return;
    }

    if (searchFilterValues.sortBy?.value !== 'DELIVERY_REQUEST_CODE_DESC') {
      dialogService.addDialog({
        children: '정렬 기준을 출고번호로 변경하고\n주문 취소를 진행해 주세요.',
      });
      return;
    }

    const unCancelableStatusCodes = new Set<TypeDeliveryAgencyOrderStatus>([
      DELIVERY_AGENCY_ORDER_STATUS.ON_THE_WAY.value,
      DELIVERY_AGENCY_ORDER_STATUS.DELIVERY_COMPLETED.value,
      DELIVERY_AGENCY_ORDER_STATUS.CANCELED.value,
      DELIVERY_AGENCY_ORDER_STATUS.DELIVERY_FAILED.value,
    ]);

    const isNothingCancelable = selectedItems.every((item) => unCancelableStatusCodes.has(item.orderStatus.code));
    if (isNothingCancelable) {
      dialogService.addDialog({
        children: `선택한 주문 ${selectedItems.length}건 모두 주문 취소 불가한 주문입니다.`,
      });
      return;
    }

    const unCancelableItems = selectedItems.filter((item) => unCancelableStatusCodes.has(item.orderStatus.code));
    if (unCancelableItems.length > 0) {
      dialogService.addDialog({
        type: 'confirm',
        children: `취소 불가한 주문이 ${unCancelableItems.length}개 포함되어 있습니다.\n불가 건 제외 후 취소를 진행하시겠습니까?\n취소 후 상태를 다시 되돌릴 수 없습니다.`,
        onConfirm: () => {
          const cancelableItems = selectedItems.filter((item) => !unCancelableStatusCodes.has(item.orderStatus.code));
          cancelOrderRequest(cancelableItems);
        },
      });
      return;
    }

    dialogService.addDialog({
      type: 'confirm',
      children: `선택한 주문 ${selectedItems.length}건을 주문 취소하시겠습니까?\n취소 후 상태를 다시 되돌릴 수 없습니다.`,
      onConfirm: () => {
        cancelOrderRequest(selectedItems);
      },
    });
  };

  const cancelOrderRequest = (requestItems: TypeDeliveryAgencyOrder[]) => {
    requestOrdersCancel({
      params: {
        orderIds: requestItems.map((item) => item.orderId),
      },
      onSuccess: (data) => {
        if (data === null) {
          toastService.addToast({ message: '주문 취소되었습니다.' });
        } else {
          dialogService.addDialog({ children: data.message });
        }

        setSelectedItems([]);
        handleSearch();
      },
      onError: (error) => {
        if (error?.code === DELIVERY_AGENCY.BACKEND_ERROR_DIALOG_CODE) {
          dialogService.addDialog({ children: error?.message });
          return;
        }

        toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
      },
    });
  };

  const handleOpenOrderExcelUpload = () => {
    const currentHour = dayjs().hour();

    if (currentHour >= 22) {
      dialogService.addDialog({
        children: '22시~00시에는 주문을 등록할 수 없습니다.\n00시 이후 다시 시도해주세요.',
      });
      return;
    }

    orderExcelUploadModal.handleOpenModal();
  };

  const handleExcelTemplateDownload = () => {
    requestTemplatesDownloadUrl({
      pathParams: { type: 'ORDER_UPLOAD' },
      onSuccess: (url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
        toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS });
      },
      onError: (error) => {
        toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
      },
    });
  };

  const handleClickDeliveryRequestConfirm = () => {
    dialogService.addDialog({
      type: 'confirm',
      children: (
        <DialogMessage
          mainMessage={
            '합포장이 가능한 주문은 자동으로 합포장이\n적용됩니다. 여러 박스에 나눠 포장하셔야 할 경우\n[추가 운송장 생성] 기능을 통해 해당 주문의\n운송장을 추가로 생성해 주세요.'
          }
          captionMessage={
            '합포장 기준:\n배송예정일, 수령자, 수령자 연락처, 도로명 기본주소, 상세주소, 배송유형이 같은 주문'
          }
        />
      ),
      onConfirm: async () => {
        await requestOutboundUnitsConfirm({
          onSuccess: (data) => {
            if (data === null) {
              toastService.addToast({ message: '출고 단위가 확정되었습니다.' });
            } else {
              dialogService.addDialog({ children: data.message });
            }
            handleSearch();
          },
          onError: (error) => {
            if (error?.code === DELIVERY_AGENCY.BACKEND_ERROR_DIALOG_CODE) {
              dialogService.addDialog({ children: error?.message });
              return;
            }

            toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
          },
        });
      },
    });
  };

  const handleConfirmExcelDownload = (reason: string) => {
    const params: TypePostOrdersDownloadParams = {
      downloadReason: reason,
      input: {
        searchKeywordType: searchedFilter.keywordType?.value ?? '',
        searchKeyword: searchedFilter.keyword,
        ...addIf(formatDate(searchedFilter.deliveryDate?.start, 'YYYY-MM-DD'), 'startDate'),
        ...addIf(formatDate(searchedFilter.deliveryDate?.end, 'YYYY-MM-DD'), 'endDate'),
        ...addIf(searchedFilter.orderStatus?.value, 'orderStatus'),
        ...addIf(searchedFilter.deliveryType?.value, 'deliveryType'),
        ...addIf(searchedFilter.courier?.value, 'courierCode'),
        sortBy: searchedFilter.sortBy?.value,
      },
    };

    requestOrdersDownload(params, {
      onSuccess: () => {
        orderExcelDownloadModal.handleConfirmModal();
        toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS });
      },
      onError: (error) => {
        toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
      },
    });
  };

  return (
    <PageWrapper>
      <PageTitle
        title="배송 주문 관리"
        breadcrumbs={BREAD_CRUMB[PATH.DELIVERY_AGENCY_ORDER]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <>
          <DeliveryAgencyOrderFilter
            options={searchFilterOptions}
            values={searchFilterValues}
            valuesStatus={searchFilterValuesStatus}
            onChange={(values) => setSearchFilterValues((prev) => ({ ...prev, ...values }))}
            onChangeStatus={(status) => setSearchFilterValuesStatus((prev) => ({ ...prev, ...status }))}
            onSearch={() =>
              handleSearch({
                ...searchFilterValues,
                pagination: {
                  ...searchFilterValues.pagination,
                  currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
                },
              })
            }
            onReset={handleResetFilterValues}
          />
          <Divider color="gray100" />
        </>
      )}
      <DeliveryAgencyOrderTable
        data={ordersData}
        pagination={searchFilterValues.pagination}
        selectedItems={selectedItems}
        isDownloadReady={isDownloadReady}
        sortOptions={searchFilterOptions.sortOptions}
        sortValue={searchFilterValues.sortBy}
        onChangeSort={handleChangeSort}
        onChangePageLimit={handleChangePageLimit}
        onChangePage={handleChangePage}
        onChangeRowSelection={handleChangeRowSelection}
        onClickDeliveryRequestConfirm={handleClickDeliveryRequestConfirm}
        onClickOrderRequestCancel={handleOrderRequestCancel}
        onClickViewDetail={handleOpenDetailModal}
        onClickOrderExcelDownload={orderExcelDownloadModal.handleOpenModal}
        onClickOrderExcelUpload={handleOpenOrderExcelUpload}
        onClickExcelTemplateDownload={handleExcelTemplateDownload}
      />
      {deliveryOrderDetailModal.isOpen && orderDetailData && (
        <DeliveryAgencyOrderDetailModal
          data={orderDetailData}
          open={deliveryOrderDetailModal.isOpen}
          onClose={deliveryOrderDetailModal.handleCloseModal}
        />
      )}
      {orderExcelDownloadModal.isOpen && (
        <DeliveryAgencyExcelDownloadModal
          type="주문"
          open={orderExcelDownloadModal.isOpen}
          onCancel={orderExcelDownloadModal.handleCloseModal}
          onConfirm={(reason) => handleConfirmExcelDownload(reason)}
        />
      )}
      {orderExcelUploadModal.isOpen && (
        <DeliveryAgencyOrderExcelUploadModal
          open={orderExcelUploadModal.isOpen}
          onCancel={orderExcelUploadModal.handleCloseModal}
          onConfirm={orderExcelUploadModal.handleConfirmModal}
        />
      )}
      {orderExcelUploadFailModal.isOpen && orderExcelUploadFailData && (
        <DeliveryAgencyOrderExcelUploadFailModal
          data={orderExcelUploadFailData}
          open={orderExcelUploadFailModal.isOpen}
          onClose={orderExcelUploadFailModal.handleCloseModal}
        />
      )}
    </PageWrapper>
  );
};

export default DeliveryAgencyOrderPage;
